import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styles from './Home.module.css';

import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./driver-theme.css";

import pic from "../../../assets/pic/pic1.png";
import arrow from "../../../assets/pic/arrow.svg";
import search from "../../../assets/pic/search.svg";
import trip from "../../../assets/pic/trip.svg";
import pic2 from "../../../assets/pic/pic2.png";
import pic3 from "../../../assets/pic/pic3.png";
import arrow3 from "../../../assets/pic/arrow3.svg";
import dogejaCat from "../../../assets/pic/dogeja_cat1.png";

import ReccoDetail from "../../../components/Home/ReccoDetail";
import PopularDetail from "../../../components/Home/PopularDetail";
import SearchOverlay from "../../../components/Home/SearchOverlay";

import { getProfile } from '../../../apis/api';

import useAuthStore from '../../../stores/useAuthStore';

const Home = () => {
    const navigate = useNavigate();


    // UI State
    const [isReccoExpanded, setIsReccoExpanded] = useState(false);
    const [isPopularExpanded, setIsPopularExpanded] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [accountKey, setAccountKey] = useState(null);



    // Initial Load: Get Profile for AutoSave setting & Name correction
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const profile = await getProfile();

                // OAuth/직접 진입 등에서 프로필 설정 플로우가 누락될 수 있어 안전장치로 처리
                if (!profile?.nickname || !profile?.birthDate) {
                    navigate('/profile-setup');
                    return;
                }

                // Stable account key for onboarding flags
                const stableKey = profile?.username || profile?.userId || profile?.id || profile?.email;
                if (stableKey) setAccountKey(stableKey);

                // [Auto Correction] If current user name is 'Social User', update it with real profile name
                const { user, login, token } = useAuthStore.getState();
                const realName = profile?.nickname || profile?.username || profile?.name;

                if (realName && user?.username !== realName) {
                    console.log("Updating User Name to:", realName);
                    // If token is missing in store (rare), try localStorage or just pass null (login might need token)
                    const currentToken = token || localStorage.getItem('accessToken');
                    if (currentToken) {
                        login(currentToken, realName);
                    }
                }


            } catch (err) {
                console.error("Failed to fetch profile settings", err);
            }
        };
        fetchProfile();
    }, [navigate]);

    // Onboarding Tour
    useEffect(() => {
        const { user } = useAuthStore.getState();
        const username = user?.username;

        const key = accountKey || username;
        if (!key) return; // Wait for user/profile

        const pending = localStorage.getItem('pendingOnboardingTour') === 'true';
        const pendingFor = localStorage.getItem('pendingOnboardingTourUsername');
        const isEligible = pending && (!pendingFor || pendingFor === key);

        const storageKey = `hasSeenOnboarding_${key}`;
        const hasSeenOnboarding = localStorage.getItem(storageKey);

        console.log(`[Onboarding] User: ${key}, Eligible: ${isEligible}, Key: ${storageKey}, Seen: ${hasSeenOnboarding}`);

        // Show tour only for newly signed-up / first-setup users
        if (isEligible && !hasSeenOnboarding && !isSearchMode) {
            const driverObj = driver({
                showProgress: true,
                animate: true,
                doneBtnText: '시작하기',
                nextBtnText: '다음',
                prevBtnText: '이전',
                allowClose: false,
                steps: [
                    {
                        element: '#onboarding-welcome',
                        popover: {
                            title: '환영합니다! 👋',
                            description: `<div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 100px; height: auto; margin-bottom: 10px;" />
                                <div>TOKPLAN에 오신 것을 환영합니다!<br/>여행 계획을 더 쉽고 스마트하게 만들어보세요.</div>
                            </div>`,
                            side: "bottom",
                            align: 'start'
                        }
                    },
                    {
                        element: '#onboarding-start-trip',
                        popover: {
                            title: '여행 계획 시작하기 ✈️',
                            description: `<div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 80px; height: auto;" />
                                <div>친구를 초대하고 대화하며<br/> ai와 함께 여행 계획을 세워보세요.</div>
                            </div>`,
                            side: "bottom",
                            align: 'center'
                        }
                    },
                    {
                        element: '#onboarding-search-bar',
                        popover: {
                            title: '여행지 검색 🔍',
                            description: `<div style="display: flex; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>궁금한 여행지가 있다면<br/>여기서 바로 검색해보세요.</div>
                            </div>`,
                            side: "bottom",
                            align: 'center'
                        }
                    },
                    {
                        element: '#onboarding-style-setting',
                        popover: {
                            title: '맞춤 추천 & 스타일 설정 🎨',
                            description: `<div style="display: flex; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>나만의 여행 스타일을 설정하고<br/>딱 맞는 여행지를 추천받을 수 있어요.</div>
                            </div>`,
                            side: "top",
                            align: 'center'
                        }
                    },
                    {
                        element: '#onboarding-popular-dest',
                        popover: {
                            title: '인기 여행지 🔥',
                            description: `<div style="display: flex; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>요즘 가장 핫한 여행지는 어디일까요?<br/>여기서 확인해보세요!</div>
                            </div>`,
                            side: "top",
                            align: 'center'
                        }
                    },
                    {
                        element: '#nav-home',
                        popover: {
                            title: '홈 화면 🏠',
                            description: `<div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>언제든 홈으로 돌아오고 싶다면<br/>이 버튼을 눌러주세요.</div>
                            </div>`,
                            side: "top",
                            align: 'center'
                        }
                    },
                    {
                        element: '#nav-chat',
                        popover: {
                            title: '채팅 목록 💬',
                            description: `<div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>참여 중인 여행 채팅방들을<br/>여기서 모아볼 수 있어요.</div>
                            </div>`,
                            side: "top",
                            align: 'center'
                        }
                    },
                    {
                        element: '#nav-trip',
                        popover: {
                            title: '내 여행 일정 🗓️',
                            description: `<div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>확정된 여행 일정과 세부 계획을<br/>확인하고 관리하세요.</div>
                            </div>`,
                            side: "top",
                            align: 'center'
                        }
                    },
                    {
                        element: '#nav-mypage',
                        popover: {
                            title: '마이페이지 👤',
                            description: `<div style="display: flex; flex-direction: column; align-items: center; gap: 10px;">
                                <img src="${dogejaCat}" alt="Guide" style="width: 60px; height: auto;" />
                                <div>내 프로필과 설정을 변경하고<br/>지난 여행 기록도 찾아보세요.</div>
                            </div>`,
                            side: "top",
                            align: 'center'
                        }
                    }
                ],
                onDestroyed: () => {
                    localStorage.setItem(storageKey, 'true');
                    localStorage.removeItem('pendingOnboardingTour');
                    localStorage.removeItem('pendingOnboardingTourUsername');
                }
            });

            setTimeout(() => {
                driverObj.drive();
            }, 500);
        }
        // Cleanup stray pending flag if user already saw tour
        if (isEligible && hasSeenOnboarding) {
            localStorage.removeItem('pendingOnboardingTour');
            localStorage.removeItem('pendingOnboardingTourUsername');
        }
    }, [isSearchMode, accountKey]);

    const handleReccoToggle = () => setIsReccoExpanded(prev => !prev);
    const handleDetailClose = () => setIsReccoExpanded(false);
    const handlePopularToggle = () => setIsPopularExpanded(prev => !prev);
    const handlePopularClose = () => setIsPopularExpanded(false);

    // Search Mode Handlers
    const openSearchMode = () => setIsSearchMode(true);
    const closeSearchMode = () => setIsSearchMode(false);



    return (
        <div className={styles.main__wrapper}>
            <div className={styles.main__container}>

                {/* Regular Home View */}
                {!isSearchMode && (
                    <>
                        {/* Fixed-height top card */}
                        <div className={styles.main__card}>
                            <div className={styles.card__text} id="onboarding-welcome">
                                <p className={styles.card__text1}>막막했던 여행,<br />대화로 시작하세요.</p>
                                <p className={styles.card__text2}>친구들을 초대해서 대화만으로 계획을 완성하세요.</p>
                            </div>
                            <img src={pic} alt="" className={styles.card__pic} />

                            <NavLink to='/chatlist' className={styles.item} id="onboarding-start-trip">
                                <div className={styles.card__button}>
                                    <p className={styles.card__text3}>새로운 여행계획 시작하기</p>
                                    <img src={arrow} alt="" className={styles.card__arrow} />
                                </div>
                            </NavLink>
                        </div>

                        {/* Search Bar - Activates Search Mode */}
                        <div className={styles.searchBar} onClick={openSearchMode} id="onboarding-search-bar">
                            <button type="button" className={styles.searchBar__iconButton} aria-label="여행지 검색">
                                <img src={search} alt="" className={styles.searchBar__icon} />
                            </button>
                            <input
                                className={styles.searchBar__input}
                                type="text"
                                placeholder='여행지 검색하기'
                                readOnly
                            />
                        </div>

                        {isReccoExpanded ? (
                            <ReccoDetail className={styles.detailClass} onClose={handleDetailClose} />
                        ) : (
                            <div className={styles.cardsContainer} id="onboarding-style-setting">
                                <div className={styles.recco} onClick={handleReccoToggle}>
                                    <p className={styles.recco__text}>맞춤<br />여행지<br />추천</p>
                                    <img src={trip} alt="" className={styles.recco__img} />
                                </div>

                                <div className={styles.cards}>
                                    <div
                                        className={styles.style}
                                        onClick={() => navigate('/mypage/edit-style')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <p className={styles.style__text}>여행 스타일<br />설정하기</p>
                                    </div>
                                    <img
                                        src={pic2}
                                        alt=""
                                        className={styles.pic2}
                                        onClick={() => navigate('/dogeja')}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                        )}

                        {isPopularExpanded ? (
                            <PopularDetail onClose={handlePopularClose} />
                        ) : (
                            <div className={styles.popular} id="onboarding-popular-dest">
                                <img src={pic3} className={styles.pic3} alt="popular travel" />
                                <p className={styles.popular__text}>이번주 인기 여행지</p>
                                <div className={styles.popular__button} onClick={handlePopularToggle}>
                                    <img src={arrow3} alt="" className={styles.popular__arrow} />
                                </div>
                            </div>
                        )}

                        <div>
                            <p className={styles.copyright}>Copyright © 2025 tokplan<br />
                                All rights reserved.</p>
                        </div>
                    </>
                )}

                {/* Search Mode View */}
                {isSearchMode && (
                    <SearchOverlay onClose={closeSearchMode} />
                )}

            </div>
        </div>
    )
}

export default Home;